import type { ReactNode } from 'react';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import type { UserProfile } from '@/lib/api/user-profile';
import { userProfileApi } from '@/lib/api/user-profile';
import { OnboardingModal } from '@/components/onboarding/OnboardingModal';
import { ExtendedOnboardingModal } from '@/components/onboarding/ExtendedOnboardingModal';
import { extendedOnboardingApi, OnboardingStatus } from '@/lib/api/extended-onboarding';

interface OnboardingContextType {
  isOnboardingRequired: boolean;
  isOnboardingOpen: boolean;
  isExtendedOnboardingOpen: boolean;
  userProfile: UserProfile | null;
  refetchProfile: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};

interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingProvider = ({ children }: OnboardingProviderProps) => {
  const { isAuthenticated, user, loading } = useAuth();
  const queryClient = useQueryClient();
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const [isExtendedOnboardingOpen, setIsExtendedOnboardingOpen] = useState(false);

  // Fetch user profile to check onboarding status
  const { data: userProfile, refetch: refetchProfile } = useQuery({
    queryKey: ['user-profile-onboarding', user?.id],
    queryFn: () => userProfileApi.getMyProfile(),
    enabled: isAuthenticated && !loading && !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch extended onboarding status
  const { data: extendedOnboardingStatus } = useQuery({
    queryKey: ['extended-onboarding-status', user?.id],
    queryFn: () => extendedOnboardingApi.getStatus(),
    enabled: isAuthenticated && !loading && !!user && userProfile?.onboarding_completed === true,
    staleTime: 5 * 60 * 1000,
  });

  // Determine if basic onboarding is required
  const isOnboardingRequired = Boolean(
    isAuthenticated &&
    !loading &&
    user &&
    userProfile &&
    !userProfile.onboarding_completed
  );

  // Determine if extended onboarding should be shown
  const isExtendedOnboardingRequired = Boolean(
    isAuthenticated &&
    !loading &&
    user &&
    userProfile?.onboarding_completed &&
    extendedOnboardingStatus?.extended_onboarding_enabled &&
    !extendedOnboardingStatus?.onboarding_completed &&
    !extendedOnboardingStatus?.onboarding_skipped
  );

  // Open basic onboarding modal when required
  useEffect(() => {
    if (isOnboardingRequired) {
      const timer = setTimeout(() => {
        setIsOnboardingOpen(true);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setIsOnboardingOpen(false);
    }
  }, [isOnboardingRequired]);

  // Open extended onboarding after basic is complete
  useEffect(() => {
    if (isExtendedOnboardingRequired && !isOnboardingOpen) {
      const timer = setTimeout(() => {
        setIsExtendedOnboardingOpen(true);
      }, 500);
      return () => clearTimeout(timer);
    } else if (!isExtendedOnboardingRequired) {
      setIsExtendedOnboardingOpen(false);
    }
  }, [isExtendedOnboardingRequired, isOnboardingOpen]);

  // Handle basic onboarding completion
  const handleOnboardingComplete = useCallback(async () => {
    // Invalidate and refetch profile to get updated data BEFORE closing modal
    // This ensures the new gender/orientation preferences are available for the swipe algorithm
    await queryClient.invalidateQueries({ queryKey: ['user-profile-onboarding'] });
    await queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    await queryClient.invalidateQueries({ queryKey: ['extended-onboarding-status'] });

    // Wait for profile to be refetched with new data
    await refetchProfile();

    // Small delay to ensure queries are propagated throughout the app
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Now close the modal - user will navigate with updated profile data
    setIsOnboardingOpen(false);
  }, [queryClient, refetchProfile]);

  // Handle extended onboarding completion
  const handleExtendedOnboardingComplete = useCallback(() => {
    setIsExtendedOnboardingOpen(false);
    queryClient.invalidateQueries({ queryKey: ['extended-onboarding-status'] });
  }, [queryClient]);

  // Determine gender filter for extended onboarding based on user profile
  const getGenderFilter = (): 'female' | 'male' | 'any' => {
    if (!userProfile) return 'female';
    const gender = userProfile.gender;
    const orientation = userProfile.sexual_orientation;

    // Based on gender and orientation, determine what characters to show
    if (gender === 'male') {
      if (orientation === 'Hetero') return 'female';
      if (orientation === 'Schwul') return 'male';
      return 'any'; // Bi
    } else {
      // female user
      if (orientation === 'Hetero') return 'male';
      if (orientation === 'Schwul') return 'female';
      return 'any'; // Bi
    }
  };

  return (
    <OnboardingContext.Provider
      value={{
        isOnboardingRequired,
        isOnboardingOpen,
        isExtendedOnboardingOpen,
        userProfile: userProfile ?? null,
        refetchProfile,
      }}
    >
      {children}
      <OnboardingModal
        open={isOnboardingOpen}
        onComplete={handleOnboardingComplete}
      />
      <ExtendedOnboardingModal
        open={isExtendedOnboardingOpen}
        onComplete={handleExtendedOnboardingComplete}
        onSkip={handleExtendedOnboardingComplete}
      />
    </OnboardingContext.Provider>
  );
};
